import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { fetchRoleContext } from '../../services/roleContext'
import { tournamentApi } from '../../services/api'
import LanguageToggle from '../../components/LanguageToggle'
import { useLanguage } from '../../context/LanguageContext'
import { setActingAccountId } from '../../services/actingAccount'
import { RESELLER_MENU_ITEMS } from '../../config/resellerMenu'
import TermsAcceptModal from '../../components/TermsAcceptModal'

/**
 * ResellerShell — sidebar layout for the reseller-facing portal (/portal/reseller/*).
 *
 * Verifies role === 'reseller' on mount; redirects elsewhere if not.
 * Shows the reseller account name in the header so it's always clear which
 * account the user is operating in (matters more once we support multiple
 * accounts per user).
 *
 * Phase 2 nav items: Dashboard, Brand, Email Integration. Phases 3-11 add
 * Connected Courses, Payment Setup, Groupings, Packages, Bookings, Reviews,
 * Custom Domain, etc.
 */
export default function ResellerShell({ children, title, activeNav }) {
  const { t } = useLanguage()
  const { user, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [ctx, setCtx] = useState(null)
  const [checked, setChecked] = useState(false)
  const [error, setError] = useState(null)
  const [inboxBadge, setInboxBadge] = useState({ unread: 0, changeRequests: 0 })
  // EPIC #92 P5 — account snapshot: drives the act-as switcher + the
  // "you are managing X" banner + the (effective) account name shown here.
  const [me, setMe] = useState(null)
  // Terms of Use gate — { currentVersion, accepted }. Blocking modal until the
  // logged-in user accepts the current version. Fails open on error so a
  // transient API blip doesn't lock anyone out.
  const [terms, setTerms] = useState(null)

  useEffect(() => {
    let alive = true
    tournamentApi.get('/portal/reseller/me')
      .then(r => { if (alive) setMe(r.data) })
      .catch(() => { if (alive) setMe(null) })
    return () => { alive = false }
  }, [location.pathname])

  useEffect(() => {
    let alive = true
    fetchRoleContext()
      .then(c => {
        if (!alive) return
        if (c?.role !== 'reseller') {
          navigate(c?.redirect || '/portal', { replace: true })
          return
        }
        setCtx(c); setChecked(true)
      })
      .catch(err => {
        if (!alive) return
        setChecked(true)
        if (err?.response?.status === 401) navigate('/login')
        else setError(err?.message || t('sp_could_not_verify_role', 'Could not verify your role'))
      })
    return () => { alive = false }
  }, [navigate])

  // Terms of Use acceptance gate. Once the reseller role is verified, check
  // whether this user has accepted the current published Terms version.
  useEffect(() => {
    if (!ctx) return
    let alive = true
    tournamentApi.get('/portal/reseller/terms/status')
      .then(r => { if (alive) setTerms(r.data) })
      .catch(() => { if (alive) setTerms({ accepted: true }) }) // fail open
    return () => { alive = false }
  }, [ctx])

  // Inbox badge polling. Cheap aggregate counts every 30s so the
  // Messages / Change requests links show fresh unread counts without
  // a heavy websocket setup. Pauses while the tab is hidden.
  useEffect(() => {
    if (!ctx) return
    let alive = true
    let timer = null
    async function refresh() {
      if (document.hidden) return
      try {
        const [msgRes, crRes] = await Promise.all([
          tournamentApi.get('/portal/reseller/messages'),
          tournamentApi.get('/portal/reseller/change-requests', { params: { status: 'open' } }),
        ])
        if (!alive) return
        const unread = msgRes.data.totalUnread || 0
        const cr = (crRes.data.requests || []).length
        setInboxBadge({ unread, changeRequests: cr })
      } catch (_) { /* silent — badge is best-effort */ }
    }
    refresh()
    timer = setInterval(refresh, 30000)
    return () => { alive = false; clearInterval(timer) }
  }, [ctx, location.pathname])

  if (!user) return <Navigate to="/login" replace />
  if (!checked) return <Center>{t('sp_verifying_access', 'Verifying access…')}</Center>
  if (error || !ctx) return <Center>{error || t('sp_access_error', 'Access error')}</Center>

  const isActive = (p) => location.pathname === p || location.pathname.startsWith(p + '/')

  // Stay & Play groupings and bundle groups share the same
  // /portal/reseller/groupings/:id route, so the URL alone can't tell them
  // apart. The grouping editor passes activeNav ('groupings' | 'bundles')
  // so the correct "Build" item highlights. When not supplied, fall back to
  // path matching (every other page).
  const groupingsActive = activeNav ? activeNav === 'groupings' : isActive('/portal/reseller/groupings')
  const bundlesActive   = activeNav === 'bundles' || isActive('/portal/reseller/golf-bundles')

  // EPIC #92 P5 — act-as state. The ENTRY point (Manage this account) lives on
  // the Managed accounts page; here we only render the "managing X" banner and
  // its exit. The effective account name reflects the child while acting-as.
  const actingAsParent = !!me?.actingAsParent
  const effectiveAccountName = me?.account?.Name || ctx.resellerAccount.name

  // EPIC #92 — parent-controlled menu visibility for managed children. The
  // parent can hide ANY sidebar item from the child; getMe returns the hidden
  // set (hiddenNavKeys). A parent acting-as the child still sees everything
  // (they configured it), and top-level resellers hide nothing. While `me` is
  // still loading we show everything to avoid hiding items from a normal
  // reseller on first paint.
  const hiddenNav = new Set(actingAsParent ? [] : (me?.hiddenNavKeys || []))
  const showNav = (key) => !hiddenNav.has(key)
  const sectionVisible = (section) =>
    RESELLER_MENU_ITEMS.some(i => i.section === section && showNav(i.key))
  function exitToOwnAccount() {
    setActingAccountId(null)
    window.location.assign('/portal/reseller')
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex' }}>
      {terms && terms.accepted === false && (
        <TermsAcceptModal
          pending={terms.pending || []}
          onAccepted={() => setTerms(prev => ({ ...prev, accepted: true, pending: [] }))}
        />
      )}
      {/* Sidebar */}
      <aside style={{ width: 240, background: '#0f172a', color: '#cbd5e1', display: 'flex', flexDirection: 'column', padding: '24px 0' }}>
        <div style={{ padding: '0 20px 24px', borderBottom: '1px solid #1e293b' }}>
          <Link to="/portal/reseller" style={{ color: '#fff', fontWeight: 700, fontSize: 18, textDecoration: 'none', display: 'block' }}>
            {effectiveAccountName}
          </Link>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, letterSpacing: 1 }}>
            {actingAsParent
              ? t('sp_managing_as_parent', 'MANAGING (as partner)')
              : <>{t('sp_reseller_portal', 'RESELLER PORTAL')}{ctx.resellerAccount.isOwner ? ' ' + t('sp_owner_suffix', '· OWNER') : ''}</>}
          </div>
        </div>
        <nav style={{ flex: 1, padding: '16px 0' }}>
          <NavItem to="/portal/reseller"                    label={t('dashboard', 'Dashboard')}                       active={location.pathname === '/portal/reseller'} />
          {sectionVisible('setup') && <SectionLabel>{t('setup', 'Setup')}</SectionLabel>}
          {showNav('brand') && (
            <NavItem to="/portal/reseller/brand"            label={t('sp_brand_profile_title', 'Brand profile')}      active={isActive('/portal/reseller/brand')} />
          )}
          {showNav('emailIntegration') && (
            <NavItem to="/portal/reseller/email-integration" label={t('sp_email_integration_title', 'Email integration')} active={isActive('/portal/reseller/email-integration')} />
          )}
          {showNav('connectedCourses') && (
            <NavItem to="/portal/reseller/courses"          label={t('sp_connected_courses_title', 'Connected courses')} active={isActive('/portal/reseller/courses')} />
          )}
          {showNav('payment') && (
            <NavItem to="/portal/reseller/payment"          label={t('sp_payment_setup_title', 'Payment setup')}      active={isActive('/portal/reseller/payment')} />
          )}
          {showNav('apiAccess') && (
            <NavItem to="/portal/reseller/api-access"       label={t('sp_api_access', 'API access')}                  active={isActive('/portal/reseller/api-access')} />
          )}
          {showNav('team') && (
            <NavItem to="/portal/reseller/team"             label={t('sp_nav_team', 'Team')}                          active={isActive('/portal/reseller/team')} />
          )}
          {sectionVisible('build') && <SectionLabel>{t('sp_nav_section_build', 'Build')}</SectionLabel>}
          {showNav('groupings') && (
            <NavItem to="/portal/reseller/groupings"        label={t('sp_nav_groupings_packages', 'Stay & Play Packages')} active={groupingsActive} />
          )}
          {showNav('golfBundles') && (
            <NavItem to="/portal/reseller/golf-bundles"     label={t('sp_nav_golf_bundles', 'Golf Bundles')}          active={bundlesActive} />
          )}
          {showNav('customTrips') && (
            <NavItem to="/portal/reseller/custom-trips"     label={t('sp_nav_custom_trips', 'Custom Trips')}          active={isActive('/portal/reseller/custom-trips')} />
          )}
          {showNav('netRateProperties') && (
            <NavItem to="/portal/reseller/net-rate-properties" label={t('sp_nav_net_rate', 'Net-rate properties')}   active={isActive('/portal/reseller/net-rate-properties')} />
          )}
          {showNav('promoCodes') && (
            <NavItem to="/portal/reseller/promo-codes"      label={t('sp_nav_promo_codes', 'Promo Codes')}            active={isActive('/portal/reseller/promo-codes')} />
          )}
          {showNav('managedAccounts') && (
            <NavItem to="/portal/reseller/managed-accounts" label={t('sp_managed_accounts', 'Managed accounts')}      active={isActive('/portal/reseller/managed-accounts')} />
          )}
          {me?.account?.ChildTermsAuthoringEnabled ? (
            <NavItem to="/portal/reseller/child-terms"    label={t('sp_child_terms_nav', 'Child Terms')}            active={isActive('/portal/reseller/child-terms')} />
          ) : null}
          {sectionVisible('operate') && <SectionLabel>{t('sp_nav_section_operate', 'Operate')}</SectionLabel>}
          {showNav('bookings') && (
            <NavItem to="/portal/reseller/bookings"         label={t('sp_nav_bookings', 'Bookings')}                  active={isActive('/portal/reseller/bookings')} />
          )}
          {showNav('toBook') && (
            <NavItem to="/portal/reseller/to-book"          label={t('sp_nav_to_book', 'To book')}                    active={isActive('/portal/reseller/to-book')} />
          )}
          {showNav('coursePayments') && (
            <NavItem to="/portal/reseller/course-payments"  label={t('sp_nav_course_payments', 'Course payments')}    active={isActive('/portal/reseller/course-payments')} />
          )}
          {/* Margin Inspector — only when InteGolf super-admin has enabled it for this account. */}
          {me?.account?.MarginInspectorEnabled ? (
            <NavItem to="/portal/reseller/margin-inspector" label={t('sp_nav_margin_inspector', 'Margin Inspector')}  active={isActive('/portal/reseller/margin-inspector')} />
          ) : null}
          {showNav('messages') && (
            <NavItem to="/portal/reseller/messages"         label={t('sp_nav_messages', 'Messages')}                  active={isActive('/portal/reseller/messages')} badge={inboxBadge.unread} />
          )}
          {showNav('changeRequests') && (
            <NavItem to="/portal/reseller/change-requests"  label={t('sp_cr_title_plural', 'Change requests')}        active={isActive('/portal/reseller/change-requests')} badge={inboxBadge.changeRequests} />
          )}
          {showNav('reviews') && (
            <NavItem to="/portal/reseller/reviews"          label={t('sp_nav_reviews', 'Reviews')}                    active={isActive('/portal/reseller/reviews')} />
          )}
          {showNav('notifications') && (
            <NavItem to="/portal/reseller/notifications"    label={t('sp_nav_notifications', 'Notifications')}        active={isActive('/portal/reseller/notifications')} />
          )}
          {showNav('emailHistory') && (
            <NavItem to="/portal/reseller/email-history"    label={t('sp_email_history', 'Email history')}            active={isActive('/portal/reseller/email-history')} />
          )}
          {showNav('customDomain') && (
            <NavItem to="/portal/reseller/domain"           label={t('sp_dash_row_custom_domain', 'Custom domain')}   active={isActive('/portal/reseller/domain')} />
          )}
        </nav>
        <div style={{ padding: '16px 20px', borderTop: '1px solid #1e293b' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>{t('language', 'Language')}</span>
            <LanguageToggle dark />
          </div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>{t('sp_signed_in_as', 'Signed in as')}</div>
          <div style={{ fontSize: 13, color: '#e2e8f0', wordBreak: 'break-all' }}>{user.email}</div>
          <button
            onClick={() => { logout(); navigate('/login') }}
            style={{ marginTop: 12, width: '100%', padding: '8px', background: 'transparent', color: '#94a3b8', border: '1px solid #334155', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}
          >{t('sp_sign_out', 'Sign out')}</button>
        </div>
      </aside>
      {/* Main */}
      <main style={{ flex: 1, padding: '32px 40px', overflowX: 'auto' }}>
        {actingAsParent && (
          <div style={{ marginBottom: 20, padding: '10px 16px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: '#92400e' }}>
              {t('sp_managing_banner', 'You are managing {name} on behalf of your partner account. Changes you make apply to their storefront.', { name: effectiveAccountName })}
            </span>
            <button type="button" onClick={exitToOwnAccount} style={{ padding: '6px 12px', background: '#92400e', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {t('sp_exit_managing', 'Exit to your account')}
            </button>
          </div>
        )}
        {title && <h1 style={{ margin: '0 0 24px', fontSize: 26, color: '#0f172a' }}>{title}</h1>}
        {children}
        <PoweredByFooter />
      </main>
    </div>
  )
}

function NavItem({ to, label, active, soon, badge }) {
  return (
    <Link
      to={to}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 20px',
        color: active ? '#fff' : (soon ? '#64748b' : '#cbd5e1'),
        background: active ? '#1e293b' : 'transparent',
        textDecoration: 'none',
        fontSize: 14,
        fontWeight: active ? 600 : 400,
        borderLeft: active ? '3px solid #00d19f' : '3px solid transparent',
        cursor: soon ? 'not-allowed' : 'pointer',
      }}
      onClick={soon ? (e) => e.preventDefault() : undefined}
      title={soon ? 'Coming in a future phase' : undefined}
    >
      <span>{label}</span>
      {soon && <span style={{ fontSize: 9, color: '#475569', letterSpacing: 1 }}>SOON</span>}
      {badge > 0 && (
        <span style={{
          display: 'inline-block',
          minWidth: 18, height: 18, padding: '0 6px',
          background: '#dc2626', color: '#fff',
          borderRadius: 999, fontSize: 11, fontWeight: 700,
          textAlign: 'center', lineHeight: '18px',
        }}>{badge > 99 ? '99+' : badge}</span>
      )}
    </Link>
  )
}

function SectionLabel({ children }) {
  return <div style={{ padding: '16px 20px 6px', fontSize: 11, color: '#475569', letterSpacing: 1.5, fontWeight: 700 }}>{children}</div>
}

function Center({ children }) {
  return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', flexDirection: 'column', gap: 12, padding: 20, textAlign: 'center' }}>{children}</div>
}

function PoweredByFooter() {
  const { t } = useLanguage()
  return (
    <div style={{ marginTop: 60, paddingTop: 24, borderTop: '1px solid #e2e8f0', textAlign: 'center', fontSize: 12, color: '#94a3b8' }}>
      {t('sp_powered_by', 'Powered by')} <a href="https://integolf.com" target="_blank" rel="noopener" style={{ color: '#00d19f', textDecoration: 'none', fontWeight: 600 }}>InteGolf</a>
      <span style={{ margin: '0 8px' }}>&middot;</span>
      <Link to="/terms" target="_blank" rel="noopener" style={{ color: '#94a3b8', textDecoration: 'none' }}>{t('sp_terms_of_use', 'Terms of Use')}</Link>
    </div>
  )
}
